import {
  differenceInCalendarDays,
  isAfter,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';
import { OrganizationSettings } from './model/organization';

export interface ReceiptUploadAllowedDayRule {
  /** Weekday index: 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun */
  dayIndex: number;
  /** How many calendar days back receipt dates may fall (any non-negative integer). */
  pastDays: number;
}

export function isSalesReceiptUploadEnabled(
  orgSettings: OrganizationSettings,
): boolean {
  return orgSettings?.enable_receipts_upload === 1;
}

function normalizeReceiptUploadAllowedDayValue(
  value: OrganizationSettings['receipts_upload_allowed_day'],
): string {
  if (value == null) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map((part) => String(part).trim()).join(',');
  }
  return String(value).trim();
}

/**
 * Parse `receipts_upload_allowed_day` as `dayIndex,pastDays`.
 * Both values are dynamic — e.g. `1,3`, `1,30`, `2,30`, `1,41`.
 * dayIndex: 0=Mon … 6=Sun. pastDays: any non-negative lookback in calendar days.
 */
export function parseReceiptUploadAllowedDay(
  orgSettings: OrganizationSettings,
): ReceiptUploadAllowedDayRule | null {
  if (!isSalesReceiptUploadEnabled(orgSettings)) {
    return null;
  }

  const normalized = normalizeReceiptUploadAllowedDayValue(
    orgSettings?.receipts_upload_allowed_day,
  );
  if (!normalized) {
    return null;
  }

  const parts = normalized.split(',').map((part) => part.trim());
  if (parts.length < 2) {
    return null;
  }

  const dayIndex = Number(parts[0]);
  const pastDays = Number(parts[1]);

  if (!Number.isInteger(dayIndex) || dayIndex < 0 || dayIndex > 6) {
    return null;
  }
  if (!Number.isInteger(pastDays) || pastDays < 0) {
    return null;
  }

  return { dayIndex, pastDays };
}

export function hasReceiptUploadAllowedDayRule(
  orgSettings: OrganizationSettings,
): boolean {
  return parseReceiptUploadAllowedDay(orgSettings) != null;
}

/** Today's weekday in backend format (Mon=0 … Sun=6). */
export function getTodayBackendDayIndex(): number {
  const today = new Date().getDay();
  return today === 0 ? 6 : today - 1;
}

/** Weekday index for a date in backend format (Mon=0 … Sun=6). */
export function getDateBackendDayIndex(date: Date): number {
  const day = date.getDay();
  return day === 0 ? 6 : day - 1;
}

/** True when today's calendar weekday matches the configured upload day. */
export function isReceiptUploadDayToday(
  orgSettings: OrganizationSettings,
): boolean {
  const rule = parseReceiptUploadAllowedDay(orgSettings);
  if (!rule) {
    return true;
  }

  return getTodayBackendDayIndex() === rule.dayIndex;
}

/** Upload (+ FAB / add receipt) allowed today. */
export function isSalesReceiptUploadAllowedToday(
  orgSettings: OrganizationSettings,
): boolean {
  if (!isSalesReceiptUploadEnabled(orgSettings)) {
    return false;
  }

  return isReceiptUploadDayToday(orgSettings);
}

/** Show + FAB when receipts can be viewed. */
export function canShowSalesReceiptFab(
  orgSettings: OrganizationSettings,
): boolean {
  return isSalesReceiptUploadEnabled(orgSettings);
}

/** Block upload when viewing is allowed but today is not an upload day. */
export function isReceiptSubmissionClosed(
  orgSettings: OrganizationSettings,
): boolean {
  return (
    isSalesReceiptUploadEnabled(orgSettings) &&
    !isSalesReceiptUploadAllowedToday(orgSettings)
  );
}

/** Date picker rules: matching weekday only, within configured past-day window. */
export function isReceiptDateSelectable(
  date: Date | null,
  orgSettings: OrganizationSettings,
): boolean {
  if (!date || !isSalesReceiptUploadEnabled(orgSettings)) {
    return false;
  }

  const today = startOfDay(new Date());
  const selected = startOfDay(date);

  if (isAfter(selected, today)) {
    return false;
  }

  const rule = parseReceiptUploadAllowedDay(orgSettings);
  if (!rule) {
    return true;
  }

  if (getDateBackendDayIndex(selected) !== rule.dayIndex) {
    return false;
  }

  const daysFromReceiptDate = differenceInCalendarDays(today, selected);

  if (rule.pastDays === 0) {
    return daysFromReceiptDate === 0;
  }

  return daysFromReceiptDate <= rule.pastDays;
}

/** Default receipt date when upload is allowed today. */
export function getDefaultReceiptDate(
  orgSettings: OrganizationSettings,
): Date | null {
  return isSalesReceiptUploadAllowedToday(orgSettings) ? new Date() : null;
}

/** Parse OCR receipt date (supports Date, ISO, and dd/MM/yyyy). */
export function parseReceiptOcrDate(value: unknown): Date | null {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }
  if (value == null || value === '') {
    return null;
  }
  const text = String(value).trim();
  for (const format of ['dd/MM/yyyy', 'yyyy-MM-dd']) {
    const parsed = parse(text, format, new Date());
    if (isValid(parsed)) {
      return parsed;
    }
  }
  const fallback = new Date(text);
  return isNaN(fallback.getTime()) ? null : fallback;
}
